import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import PhoneInput, {
  isValidPhoneNumber,
  getCountryCallingCode,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useForm, Controller, useWatch } from "react-hook-form";
import { useRouter } from "next/router";
import styles from "./RequestForm.module.css";
import { submitFormData } from "@/services/LeadService";
import useGeoLocation from "@/services/useGeoLocation";
import {
  CmsAllCousesList,
  fetchAllProgramNamesList,
  postEventUserData,
} from "@/services/CmsService";
import validator from "validator";
import OTPScreen from "../OTP/OTPScreen";
import UpdatePhone from "../OTP/UpdatePhone";
import { removeCountryCode } from "@/utils/utils";
import { sentOtpAction } from "@/services/PreEnrollmentService";
import ThankYou from "../OTP/ThankYou";
import axios from "axios";

import { Info, ChevronDown } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  trackEventPreEnrolment,
  trackFormSubmission,
  trackFormSubmissionError,
} from "@/services/mixpanel";
import {
  getStoredUrlParams,
  getUtmParams,
  removeExpiredUtm,
} from "@/utils/cookieutils";

interface Program {
  field_of_interest: string;
  program_name: string;
  slug: string;
  program_brochure?: {
    brochure_pdf_link?: {
      url: string;
    };
  };
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  Programme_Of_Interest: string;
  qualification: string;
  mode: string;
}

const qualifications = [
  "Doctorate",
  "Master's Degree",
  "Hons Degree",
  "Post Graduation",
  "Bachelor's Degree",
  "Diploma",
  "Higher Certificate",
  "Matric (Bachelor Pass)",
  "Matric",
  "Pursuing Matric",
  "Grade 12 and Below",
  "Higher National Diploma",
  "National Diploma",
  "Ordinary National Diploma",
  "KCSE (Matric)",
  "SSC",
  "BA",
];

const mode = ["Online", "Contact"];

const RequestForm = (props: any) => {
  const {
    onFilledFieldsChange,
    programOfIntrestLP,
    isProgramPage,
    onVineCrmApi,
    closePopup,
    handleStep,
    preEnrollmentEnable,
    saveDataOnVinecrm,
    title,
    BroutcherLink,
    onFormSubmit,
    programOfInterestMultipleOptionList = false,
  } = props;
  const [mobileNumber, setMobileNumber] = useState("");
  const [btnDisable, setBtnDisable] = useState(false);
  const [isValidPhoneNumberData, setIsValidPhoneNumberData] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState("");
  const [broutcherLink, setBroutcherLink] = useState<string | null>(null);
  const [step, setStep] = useState<number>(1);
  const [isFocused, setIsFocused] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [formData, setFormData] = useState<any>();
  const [slugs, setSlugs] = useState<Program[]>([]);
  const [phoneNumberError, setPhoneNumberError] = useState<string>("");
  const [programsList, setProgramsList] = useState([]);
  const [isUserExist, setIsUserExist] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const router = useRouter();
  const geoLocationData: any = useGeoLocation();
  const [openSelect, setOpenSelect] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useForm<FormData>();

  const resetForm = () => {
    setValue("name", "");
    setValue("email", "");
    setValue("phone", "");
    setValue("Programme_Of_Interest", "");
    setValue("qualification", "");
    setValue("mode", "");

    setMobileNumber("");
    setCountryCode("");
    setFormData(null);
    setBtnDisable(false);
    setIsSubmitting(false);
    setPhoneNumberError("");
    setIsValidPhoneNumberData(false);
  };

  const programmeOfInterest = watch("Programme_Of_Interest") || selectedProgram;
  const isLpPage = router.asPath.startsWith("/lp/");

  const isRegenesysEducationSlug = router.asPath === "/lp/regenesys-education";

  useEffect(() => {
    if (programmeOfInterest || selectedProgram) {
      setValue("Programme_Of_Interest", programmeOfInterest || selectedProgram);
    }
  }, [programmeOfInterest, selectedProgram, setValue]);

  const watchedFields = useWatch({
    control,
    name: [
      "name",
      "email",
      "phone",
      "Programme_Of_Interest",
      "qualification",
      "mode",
    ],
  });

  const watchedFieldsString = useMemo(
    () => watchedFields.join("|"),
    [watchedFields]
  );

  useEffect(() => {
    const fields: string[] = [];

    if (watchedFields[0]) fields.push("name");
    if (watchedFields[1]) fields.push("email");
    if (watchedFields[2]) fields.push("phone number");
    if (watchedFields[3]) fields.push("programme of interest");
    if (watchedFields[4]) fields.push("qualification");
    if (watchedFields[5]) fields.push("study mode");

    onFilledFieldsChange?.(fields);
  }, [watchedFieldsString, onFilledFieldsChange]);

  const Qualification = watch("qualification");
  localStorage.setItem(
    "userSelectedProgram",
    JSON.stringify(programmeOfInterest)
  );

  const userSelectedProgram = (programsList as any[]).find(
    (pro: any) => pro?.name == programmeOfInterest
  );

  useEffect(() => {
    if (isLpPage && programOfIntrestLP) {
      setValue("Programme_Of_Interest", programOfIntrestLP.program_name, {
        shouldValidate: true,
      });
    }
  }, [isLpPage, programOfIntrestLP, setValue]);
  if (userSelectedProgram) {
    localStorage.setItem("proCode", JSON.stringify(userSelectedProgram?.code));
  }

  const modeValue = watch("mode");

  const disabledPrograms = [
    "Bachelor of Commerce in Banking and Financial Services",
    "Doctor of Business Management",
    "Bachelor of Science in Computer Science",
  ];

  const isDisabledProgram = disabledPrograms.includes(programmeOfInterest);

  useEffect(() => {
    if (isDisabledProgram) {
      setValue("mode", "ONLINE", { shouldValidate: true });
    }
  }, [programmeOfInterest, setValue, isDisabledProgram]);

  useEffect(() => {
    if (isProgramPage === "contact") {
      setValue("mode", "CONTACT");
    } else if (isProgramPage === "online") {
      setValue("mode", "ONLINE");
    } else setValue("mode", "");
  }, [isProgramPage, setValue]);

  useEffect(() => {
    const programData = slugs.find(
      (slug) => slug.program_name === programmeOfInterest
    );
    setBroutcherLink(
      programData?.program_brochure?.brochure_pdf_link?.url || ""
    );
  }, [programmeOfInterest, slugs]);

  const { asPath } = router;
  const pathArray = asPath.split("/").filter((path) => path);
  const slugPath = pathArray[0];

  const calculateCompletionTime = (): number => {
    return Math.floor((Date.now() - 0) / 1000);
  };

  const calculateFieldCompletionRate = (
    data: FormData | null | undefined
  ): number => {
    if (!data || typeof data !== "object") return 0;

    const fieldsFilled = Object.values(data).filter(Boolean).length;
    return Math.round((fieldsFilled / Object.keys(data).length) * 100);
  };

  useEffect(() => {
    handleStep?.(step);
  }, [step, handleStep]);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const response = await CmsAllCousesList();
        const sortedData = response?.data?.sort((a: any, b: any) => {
          if (a.program_name < b.program_name) {
            return -1;
          }
          if (a.program_name > b.program_name) {
            return 1;
          }
          return 0;
        });
        setSlugs(sortedData || []);

        const matchingSlug = response?.data.find(
          (slug: Program) => slug.slug === slugPath
        );
        if (matchingSlug) {
          setSelectedProgram(matchingSlug.field_of_interest);
        }
      } catch (error) {
        console.error("Error fetching course data:", error);
      }
    };

    fetchCourseData();
  }, [slugPath]);

  useEffect(() => {
    const fetchProgramsList = async () => {
      try {
        const response = await fetchAllProgramNamesList();
        setProgramsList(response?.data?.data?.data || []);
      } catch (error) {
        console.error("Error fetching Programs List:", error);
      }
    };
    fetchProgramsList();
  }, []);

  const handleCountryCodeChange = (country: any) => {
    if (country) {
      const callingCode = getCountryCallingCode(country);
      setCountryCode(callingCode);
    } else {
      setCountryCode("");
    }
  };

  const mobileWithoutCode = removeCountryCode(countryCode, mobileNumber);

  removeExpiredUtm();

  const utmParams = useMemo(() => {
    try {
      const storedParams = getStoredUrlParams();

      if (storedParams) {
        return getUtmParams(storedParams);
      }
      return {};
    } catch (error) {
      console.error("Error processing UTM parameters:", error);
      return {};
    }
  }, []);

  const handleLeadsData = useCallback(
    async (is_otp_verified?: 0 | 1) => {
      setBtnDisable(true);

      try {
        await submitFormData(
          {
            ...(formData || {}),
            phone: mobileNumber,
            course: formData?.Programme_Of_Interest || selectedProgram,
            qualification: formData?.qualification,
            mode: modeValue,
            ...utmParams,
            page_url: window.location.href,
            City: geoLocationData?.city,
            country: geoLocationData?.countryName,
            is_otp_verified: is_otp_verified,
          },
          router
        );

        if (BroutcherLink === "Download Brochure" && broutcherLink) {
          window.open(broutcherLink || "", "_blank");
        } else if (
          BroutcherLink &&
          BroutcherLink !== "Download Brochure" &&
          typeof BroutcherLink === "string"
        ) {
          window.open(BroutcherLink, "_blank");
        }
      } catch (error: any) {
        console.error("Error submitting form data:", error);
        setBtnDisable(false);
      }
    },
    [
      formData,
      mobileNumber,
      selectedProgram,
      modeValue,
      utmParams,
      geoLocationData,
      BroutcherLink,
      broutcherLink,
      router,
    ]
  );

  useEffect(() => {
    const processLeadsData = async () => {
      if (step === 2 && onVineCrmApi) {
        try {
          await handleLeadsData(0);
          closePopup?.();
        } catch (error) {
          console.error("Error while handling leads data:", error);
        }
      }
    };

    processLeadsData();
  }, [step, onVineCrmApi, handleLeadsData, closePopup]);

  const onSubmit = async (data: any) => {
    if (!mobileNumber || !isValidPhoneNumber(mobileNumber)) {
      setIsValidPhoneNumberData(false);
      setPhoneNumberError("Valid phone number required");
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    setFormData(data);
    localStorage.setItem("formData", JSON.stringify(data));

    setIsValidPhoneNumberData(true);

    const payload = {
      mobileNumber: mobileWithoutCode,
      mobileCountryCode: countryCode,
    };

    localStorage.setItem("userPayload", JSON.stringify(payload));
    const isLpPage = router.asPath.includes("/lp");
    if (preEnrollmentEnable) {
      try {
        const [otpResponse] = await Promise.all([
          sentOtpAction(payload),
          handleLeadsData(),
        ]);

        if (otpResponse.statusCode !== 201) {
          console.error("OTP API response error:", otpResponse);
          setIsSubmitting(false);
          return;
        }

        setStep(2);
      } catch (error) {
        console.error("Error handling OTP or leads data:", error);
        setIsSubmitting(false);
      }
    }

    // Calculate form metrics and abandonment
    const completionTime = calculateCompletionTime();
    const fieldCompletionRate = calculateFieldCompletionRate(data);
    const fieldAbandonment = Object.keys(data)
      .filter((key) => !data[key as keyof FormData])
      .map((key) => key as string);

    setBtnDisable(true);
    try {
      await submitFormData(
        {
          ...data,
          course: data?.Programme_Of_Interest || selectedProgram,
          qualification: data?.qualification,
          ...utmParams,
          page_url: window.location.href,
          City: geoLocationData?.city,
          country: geoLocationData?.countryName,
          isVinecrmEnable: saveDataOnVinecrm ?? true,
        },
        router
      );

      // Handle brochure link after successful form submission
      if (BroutcherLink === "Download Brochure" && broutcherLink) {
        window.open(broutcherLink || "", "_blank");
      } else if (
        BroutcherLink &&
        BroutcherLink !== "Download Brochure" &&
        typeof BroutcherLink === "string"
      ) {
        window.open(BroutcherLink, "_blank");
      }

      if (props.saveEvenCmsData) {
        await postEventUserData({
          ...data,
          mobileNumber: data?.phone,
          course: data?.Programme_Of_Interest || selectedProgram,
          qualification: data?.qualification,
          studyMode: data?.study_mode,
          event_name: props?.EventTitle,
        });
      }

      trackFormSubmission(
        title ||
          "Awaken Your Potential with Regenesys – Start Your Journey Today!",
        completionTime,
        fieldCompletionRate,
        fieldAbandonment,
        errors,
        data,
        router.asPath
      );

      trackEventPreEnrolment("lead_form_submitted", {
        email: data?.email,
      });
      if (isLpPage) {
        const queryParams = new URLSearchParams({
          param: "newbrandSA",
          paramemail: data.email,
          paramphone: data.phone,
          paramname: data.name,
          paramcity: geoLocationData?.city,
          paramcountry: geoLocationData?.countryName,
          paramQualification: data.qualification,
          page_url: window.location.href,
        }).toString();

        window.open(`/thankyou?${queryParams}`, "_blank");
      }
    } catch (error: any) {
      console.error("Error submitting form data:", error);
      alert(
        "Sorry, there was an error submitting your form. Please try again later."
      );
      trackFormSubmissionError(
        error.message,
        props.title ||
          "Awaken Your Potential with Regenesys – Start Your Journey Today!"
      );

      setBtnDisable(false);
    } finally {
      if (isLpPage) {
        resetForm();
      }
      if (!preEnrollmentEnable) {
        onFormSubmit?.();
      } else {
        setStep(2);
      }
    }
  };

  // const handleBrochureLink = () => {
  //   if (BroutcherLink === "Download Brochure" && broutcherLink) {
  //     window.open(broutcherLink || "", "_blank");
  //   } else if (
  //     BroutcherLink &&
  //     BroutcherLink !== "Download Brochure" &&
  //     typeof BroutcherLink === "string"
  //   ) {
  //     window.open(BroutcherLink, "_blank");
  //   }
  // };

  const handlesendOTP = async () => {
    const payload = {
      mobileNumber: mobileWithoutCode,
      mobileCountryCode: countryCode,
    };

    if (payload) {
      try {
        const response = await sentOtpAction(payload);
        if (response.statusCode === 201) {
          setStep(2);
        }
      } catch (error) {
        console.error("err", error);
      }
    }

    await handleLeadsData();
  };

  const handleChangePhoneNumber = () => {
    setStep(3);
    if (mobileNumber)
      trackEventPreEnrolment("change_phone_number_clicked", {
        phone_number: mobileNumber,
      });
  };

  const handleExistingUser = async () => {
    const payload = {
      mobileNumber: mobileWithoutCode,
      mobileCountryCode: countryCode,
      email: formData?.email,
    };
    const token = process.env.PRE_ENROLLMENT_UNIVERSAL_ACCESS_TOKEN;
    const baseUrl = process.env.PRE_ENROLLMENT_BASE_URL;
    try {
      const response = await axios.post(
        `${baseUrl}pre-enrolment/student`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response?.status === 201) {
        setIsUserExist(response?.data);
        setStep(4);
        return true;
      } else {
        toast.error("Something went wrong. Please try again.");
        return false;
      }
    } catch (error) {
      console.error("error", error);
      toast.error("Failed to proceed. Please try again later.");
      return false;
    }
  };

  const formFieldsSection = {
    study_mode: props?.formFieldsSection?.study_mode ?? true,
    highest_qualification:
      props?.formFieldsSection?.highest_qualification ?? true,
    program_of_interest: props?.formFieldsSection?.program_of_interest ?? true,
  };

  return (
    <>
      {step === 1 && (
        <form
          onSubmit={handleSubmit(onSubmit)}
          data-interaction={
            handleSubmit(onSubmit) ? "Form Completed" : "Form Not Completed"
          }
          className={styles.form}
        >
          <h3 className={isLpPage ? styles.lpfromTitle : ""}>
            {title ||
              "Awaken Your Potential with Regenesys – Start Your Journey Today!"}
          </h3>
          <div
            className={`${styles.formWrapper} ${isLpPage ? styles.lpForm : ""}`}
          >
            <div className={styles.field}>
              <input
                {...register("name", {
                  required: "Name is required",
                  pattern: {
                    value: /^[A-Za-z ]+$/,
                    message: "Name can only contain letters and spaces",
                  },
                })}
                placeholder="Enter Full Name"
                autoComplete="none"
                onKeyDown={(e) => {
                  if (e.key >= "0" && e.key <= "9") {
                    e.preventDefault();
                  }
                }}
              />
            </div>
            <div className={styles.field}>
              <input
                {...register("email", { required: "Email is required" })}
                placeholder="Enter Email"
                type="email"
                autoComplete="none"
              />
            </div>
            <div className={styles.fieldInPut}>
              <Controller
                name="phone"
                control={control}
                rules={{
                  required: "Phone number is required",
                }}
                render={({ field }) => (
                  <PhoneInput
                    {...field}
                    autoComplete="none"
                    value={mobileNumber}
                    international
                    countryCallingCodeEditable={false}
                    onFocus={() => setIsFocused(true)}
                    onChange={(value) => {
                      const phoneNumber = value ? value.toString() : "";
                      const isValid = validator.isMobilePhone(
                        phoneNumber || ""
                      );
                      setMobileNumber(phoneNumber as string);
                      field.onChange(phoneNumber);

                      if (phoneNumber.replace(/[^0-9]/g, "").length >= 4) {
                        if (isValid) {
                          setMobileNumber(phoneNumber);
                          setPhoneNumberError("");
                        } else {
                          setPhoneNumberError("Valid phone number required");
                        }
                      } else {
                        setPhoneNumberError("");
                      }
                    }}
                    onCountryChange={handleCountryCodeChange}
                    defaultCountry={geoLocationData?.countryCode}
                    placeholder="Enter Phone Number"
                  />
                )}
              />
            </div>
            {props?.preEnrollmentEnable && (
              <div className={styles.noticeText}>
                {isFocused && (
                  <small className={styles.info}>
                    <Info />
                    <span className="ms-1">
                      OTP will be sent to your registered mobile number.
                    </span>
                  </small>
                )}
              </div>
            )}
            {isLpPage &&
            (programOfInterestMultipleOptionList === true ||
              isRegenesysEducationSlug) ? (
              <Controller
                name="Programme_Of_Interest"
                control={control}
                rules={{ required: "*Course is required" }}
                render={({ field }) => (
                  <div className={styles.field}>
                    <span
                      className={`${styles.dropdownIcon} ${
                        openSelect === "program" ? styles.rotate : ""
                      }`}
                    >
                      <ChevronDown />
                    </span>
                    <select
                      {...field}
                      onFocus={() => setOpenSelect("program")}
                      onBlur={() => setOpenSelect(null)}
                      onChange={(e) => {
                        field.onChange(e);
                        setOpenSelect(null);
                      }}
                      value={
                        field.value ||
                        (isRegenesysEducationSlug
                          ? ""
                          : programOfIntrestLP?.program_name) ||
                        ""
                      }
                    >
                      <option value="">Programme of interest</option>
                      {programOfIntrestLP?.program_name &&
                        !isRegenesysEducationSlug && (
                          <option value={programOfIntrestLP.program_name}>
                            {programOfIntrestLP.program_name}
                          </option>
                        )}
                      {programsList
                        ?.filter(
                          (slug: any) =>
                            isRegenesysEducationSlug ||
                            slug?.name !== programOfIntrestLP?.program_name
                        )
                        .map((slug: any, index: number) => (
                          <option key={index} value={slug?.name}>
                            {slug?.name}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              />
            ) : isLpPage &&
              (programOfInterestMultipleOptionList === false ||
                programOfInterestMultipleOptionList === null) &&
              !isRegenesysEducationSlug ? (
              <Controller
                name="Programme_Of_Interest"
                control={control}
                rules={{ required: "*Course is required" }}
                render={({ field }) => (
                  <div className={styles.field}>
                    <select {...field}>
                      {props?.programOfIntrestLP && (
                        <option
                          value={props?.programOfIntrestLP?.field_of_interest}
                        >
                          {props?.programOfIntrestLP?.program_name}
                        </option>
                      )}
                    </select>
                  </div>
                )}
              />
            ) : (
              formFieldsSection?.program_of_interest && (
                <Controller
                  name="Programme_Of_Interest"
                  control={control}
                  rules={{ required: "*Course is required" }}
                  render={({ field }) => (
                    <div className={styles.field}>
                      <span
                        className={`${styles.dropdownIcon} ${
                          openSelect === "program" ? styles.rotate : ""
                        }`}
                      >
                        <ChevronDown />
                      </span>
                      <select
                        {...field}
                        onFocus={() => setOpenSelect("program")}
                        onBlur={() => setOpenSelect(null)}
                        onChange={(e) => {
                          field.onChange(e);
                          setOpenSelect(null);
                        }}
                      >
                        <option value="">Programme of interest</option>
                        {programsList?.map((slug: any, index: number) => (
                          <option key={index} value={slug?.name}>
                            {slug?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                />
              )
            )}

            {formFieldsSection?.highest_qualification && (
              <div className={styles.field}>
                <span
                  className={`${styles.dropdownIcon} ${
                    openSelect === "qualification" ? styles.rotate : ""
                  }`}
                >
                  <ChevronDown />
                </span>
                <select
                  {...register("qualification", {
                    required: "*Qualification is required",
                  })}
                  value={Qualification}
                  onFocus={() => setOpenSelect("qualification")}
                  onBlur={() => setOpenSelect(null)}
                  onChange={(e) => {
                    setValue("qualification", e.target.value);
                    setOpenSelect(null);
                  }}
                >
                  <option value="">Highest Qualification</option>
                  {qualifications.map((qualification, index) => (
                    <option key={index} value={qualification}>
                      {qualification}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formFieldsSection?.study_mode && (
              <div className={styles.field}>
                <span
                  className={`${styles.dropdownIcon} ${
                    openSelect === "mode" ? styles.rotate : ""
                  }`}
                >
                  <ChevronDown />
                </span>
                <select
                  {...register("mode", {
                    required: "*Mode is required",
                  })}
                  disabled={isDisabledProgram}
                  onFocus={() => setOpenSelect("mode")}
                  onBlur={() => setOpenSelect(null)}
                  onChange={(e) => {
                    setValue("mode", e.target.value);
                    setOpenSelect(null);
                  }}
                >
                  <option value="" hidden>
                    Study Mode
                  </option>
                  {mode.map((m, index) => (
                    <option key={index} value={m.toLocaleUpperCase()}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {Object.keys(errors ?? {}).length > 1 ? (
              <p className={styles.error}>
                {" "}
                Required: All fields must be filled
              </p>
            ) : (
              <>
                {errors?.mode && (
                  <p className={styles.error}>{errors?.mode?.message}</p>
                )}
                {errors?.qualification && (
                  <p className={styles.error}>
                    {errors?.qualification?.message}
                  </p>
                )}
                {errors?.Programme_Of_Interest && (
                  <p className={styles.error}>
                    {errors?.Programme_Of_Interest?.message}
                  </p>
                )}
                {errors?.name && (
                  <p className={styles.error}>{errors?.name?.message}</p>
                )}
                {errors?.email && (
                  <p className={styles.error}>{errors?.email?.message}</p>
                )}
                {phoneNumberError ? (
                  <small className={styles.error}>{phoneNumberError}</small>
                ) : (
                  (mobileNumber.length === 0
                    ? !phoneNumberError
                    : phoneNumberError || isValidPhoneNumberData) &&
                  errors?.phone && (
                    <small className={styles.error}>
                      {errors?.phone?.message}
                    </small>
                  )
                )}
              </>
            )}

            <div className={styles.field}>
              <button
                type="submit"
                disabled={isSubmitting || btnDisable}
                className={` ${isSubmitting ? styles.loading : ""}`}
              >
                {isSubmitting ? "Submitting..." : props?.btnText || "Submit"}
              </button>
            </div>
          </div>
        </form>
      )}
      {props?.preEnrollmentEnable && (
        <>
          {step === 2 && (
            <OTPScreen
              handleChangePhoneNumber={handleChangePhoneNumber}
              mobileNumber={mobileNumber}
              mobileWithoutCode={mobileWithoutCode}
              countryCode={countryCode}
              resendOTP={handlesendOTP}
              handleLeadsData={handleLeadsData}
              setStep={setStep}
              handleExistingUser={handleExistingUser}
            />
          )}
          {step === 3 && (
            <UpdatePhone
              setStep={setStep}
              mobileNumber={mobileNumber}
              setMobileNumber={setMobileNumber}
              handleCountryCodeChange={handleCountryCodeChange}
              handlesendOTP={handlesendOTP}
            />
          )}
          {step === 4 && <ThankYou isUserExist={isUserExist} />}
        </>
      )}
    </>
  );
};

// Custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps: any, nextProps: any) => {
  // Only re-render if these critical props change
  return (
    prevProps.programOfInterestMultipleOptionList ===
      nextProps.programOfInterestMultipleOptionList &&
    prevProps.programOfIntrestLP === nextProps.programOfIntrestLP &&
    prevProps.isProgramPage === nextProps.isProgramPage &&
    prevProps.title === nextProps.title &&
    prevProps.btnText === nextProps.btnText
  );
};

export default memo(RequestForm, arePropsEqual);
